// A page in Work mode (M7-02; spec 06 §Main screens "Page — BlockNote document with outline,
// versions, comments as a thread"): breadcrumbs (a hidden ancestor reads "…" and is no link), the
// kind and restriction, then the title and BlockNote body (UI-01's PageEditor) with Save (and
// ⌘/Ctrl+S) as `PATCH pages/{id}` under If-Match; a 409 says someone else saved first and offers a
// reload. Read-only when the caller may not edit (`can_edit`). Actions: New sub-page, Move,
// Restrict / Lift restriction (the space's editors), History, Archive. The pages below it follow
// the body. DOCS-KB: Star and a `…` menu (Move, Archive/Restore, Copy link, Delete), Restore in
// the archived notice, and "Used by" (`pages.readers`) on an SOP, policy or KB article. Beside it, Outline (the body's headings; a click scrolls there) and Comments (the
// page's thread, a chat thread like a task's: `messages.*`).
//
// Calls on open: `pages.get` (the spaces list and the pages tree are the sidebar's, cached).
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarTile,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  type ChatMessage,
  Composer,
  type DocLinkState,
  type DocLinks,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  MessageList,
  Notice,
  PageEditor,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ChevronDownIcon,
  EllipsisIcon,
  FileTextIcon,
  FolderInputIcon,
  HistoryIcon,
  LinkIcon,
  LockIcon,
  LockOpenIcon,
  MessagesSquareIcon,
  PlusIcon,
  SaveIcon,
  Share2Icon,
  Trash2Icon,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { RememberThis } from "../memory/remember-this";
import { useFeature } from "../use-feature";
import { DocLocalGraph } from "./doc-graph";
import { DocMemoryChip } from "./doc-memory-chip";
import { UploadSourceBar } from "./doc-original";
import { RelatedPanel, useDocRelated } from "./doc-related";
import { copyDocLink, useDocWrites } from "./doc-writes";
import { DeleteDocDialog } from "./docs-screen";
import { useSeatPeople } from "./model";
import {
  kindLabel,
  MovePageDialog,
  NewDocDialog,
  type NewDocTarget,
  PAGE_KINDS,
  type PageDetail,
  PageHistoryDrawer,
  SharePageDialog,
} from "./page-dialogs";
import { isPlaybook, PlaybookStrip, playbookQuery } from "./playbook";
import { useRecordRecent } from "./recents";
import {
  invalidateWork,
  pageLinksQuery,
  pageQuery,
  pageReadersQuery,
  pageSearchQuery,
  pageSharesQuery,
  pagesQuery,
  spacesQuery,
  workKeys,
} from "./work-queries";
import { FavoriteStar } from "./work-screens";

type Draft = { title?: string; blocks?: unknown[] };

export function PageScreen({
  api,
  company,
  pageId,
}: {
  api: Api;
  company: CompanyAccess;
  pageId: string;
}) {
  const co = company.id;
  const page = useQuery(pageQuery(api, co, pageId));
  useRecordRecent(api, co, "page", page.data ? pageId : null);
  if (page.isPending)
    return (
      <div role="status" aria-label="Loading the page" className="flex max-w-3xl flex-col gap-3">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-40" />
      </div>
    );
  if (page.isError || !page.data)
    return (
      <EmptyState
        icon={FileTextIcon}
        title="This page is not available"
        titleAs="h1"
        description="It may be restricted, or in a space you are not a member of."
        actions={
          <Button
            variant="secondary"
            render={<Link to="/c/$slug/work" params={{ slug: company.slug }} search={keepFocus} />}
          >
            Back to Work home
          </Button>
        }
      />
    );
  return <PageBody api={api} company={company} page={page.data} />;
}

/**
 * A doc toolbar action that stays visible: icon and label from 2xl, icon with a tooltip below it;
 * `fold`: under lg it leaves the row for the "⋯" menu, which has it at every width.
 */
function ToolbarAction({
  icon: Icon,
  label,
  fold = false,
  disabled = false,
  onClick,
}: {
  icon: typeof HistoryIcon;
  label: string;
  fold?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className={fold ? "hidden lg:inline-flex" : undefined}
          />
        }
      >
        <Icon aria-hidden="true" />
        <span className="hidden 2xl:inline">{label}</span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function PageBody({ api, company, page }: { api: Api; company: CompanyAccess; page: PageDetail }) {
  const co = company.id;
  const queryClient = useQueryClient();
  const spaces = useQuery(spacesQuery(api, co));
  const pages = useQuery(pagesQuery(api, co));
  const space = spaces.data?.data.find((s) => s.id === page.space_id);
  const draft = useRef<Draft>({});
  const [dirty, setDirty] = useState(false);
  const [stale, setStale] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [newPage, setNewPage] = useState<NewDocTarget | null>(null);
  const [moving, setMoving] = useState(false);
  // MULTI-SPACE-ACCESS: the doc's Share dialog and the spaces it is also in.
  const [sharingOpen, setSharingOpen] = useState(false);
  const shares = useQuery(pageSharesQuery(api, co));
  const alsoIn = (shares.data?.data ?? [])
    .filter((s) => s.object_id === page.id)
    .map((s) => spaces.data?.data.find((x) => x.id === s.space_id)?.name)
    .filter((n): n is string => Boolean(n));
  const [history, setHistory] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const writes = useDocWrites(api, company);
  const navigate = useNavigate();
  const docLinks = useDocLinks(api, company, page);
  const editable = page.can_edit;
  // Wave 25 (MULTI-SPACE-ACCESS): an editor share edits the text; the page's place (sub-pages,
  // move, share, restrict, archive, delete) stays its home space's editors' — not offered.
  const manageable = editable && page.can_manage !== false;
  // An archived page is read-only but still its editors' to restore or delete.
  const deletable = (editable && page.can_manage) || Boolean(page.archived_at);

  // A fresh page (another id, or a version written elsewhere and loaded) starts a fresh draft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the page and its version only.
  useEffect(() => {
    draft.current = {};
    setDirty(false);
  }, [page.id, page.version, nonce]);

  const done = async (p: PageDetail) => {
    queryClient.setQueryData(workKeys.page(co, p.id), p);
    await invalidateWork(queryClient, co);
  };

  const save = useMutation({
    mutationFn: () =>
      api.client.pages.update({
        params: { co, id: page.id },
        ifMatch: page.version,
        body: {
          ...(draft.current.title !== undefined ? { title: draft.current.title } : {}),
          ...(draft.current.blocks !== undefined ? { body: draft.current.blocks as never } : {}),
        },
      }),
    onSuccess: async (p) => {
      toast.add({ title: "Page saved", description: `Version ${p.version}.`, type: "success" });
      await done(p);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) setStale(true);
    },
  });
  const saveNow = useCallback(() => {
    if (editable && dirty && !save.isPending) save.mutate();
  }, [editable, dirty, save]);

  // ⌘S / Ctrl+S saves (and never opens the browser's "save page").
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveNow();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saveNow]);

  const restrict = useMutation({
    mutationFn: (restricted: boolean) =>
      api.client.pages.update({
        params: { co, id: page.id },
        ifMatch: page.version,
        body: { restricted },
      }),
    onSuccess: async (p) => {
      toast.add({
        title: p.restricted ? "Only the space's editors see it now" : "The space sees it again",
      });
      await done(p);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) setStale(true);
      else
        toast.add({
          title: "The restriction did not change",
          description: e instanceof ApiError ? (e.problem?.detail ?? undefined) : undefined,
          type: "error",
        });
    },
  });

  // KB-UPLOAD-2: its managers change what it is (the kind chip) and whether it is read only.
  const manage = useMutation({
    mutationFn: (body: { kind?: PageDetail["kind"]; read_only?: boolean }) =>
      api.client.pages.update({
        params: { co, id: page.id },
        ifMatch: page.version,
        body: body as never,
      }),
    onSuccess: async (p, body) => {
      toast.add({
        title:
          body.read_only === undefined
            ? `Now a ${kindLabel(p.kind)}`
            : p.read_only
              ? "Read only for everyone but its managers"
              : "Its space can edit it again",
      });
      await done(p);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) setStale(true);
      else
        toast.add({
          title: "The doc did not change",
          description: e instanceof ApiError ? (e.problem?.detail ?? undefined) : undefined,
          type: "error",
        });
    },
  });

  const archive = useMutation({
    mutationFn: () =>
      api.client.pages.archive({
        params: { co, id: page.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async (p) => {
      toast.add({ title: `${p.title} archived`, description: "Its pages moved up a level." });
      await done(p);
    },
  });

  const reload = async () => {
    await queryClient.invalidateQueries({ queryKey: workKeys.page(co, page.id) });
    setStale(false);
    setNonce((n) => n + 1);
  };

  const taskBody = page.kind === "task_body";
  const playbook = useQuery({
    ...playbookQuery(api, co, page.id),
    enabled: !taskBody && page.kind !== "board_note",
  });
  return (
    <div data-screen="page" className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <article aria-label={page.title} className="flex min-w-0 flex-1 flex-col">
        <Breadcrumb className="mb-2">
          <BreadcrumbList>
            {space ? (
              <BreadcrumbItem>
                <BreadcrumbLink
                  render={
                    <Link
                      to="/c/$slug/work/s/$spaceId"
                      params={{ slug: company.slug, spaceId: space.id }}
                      search={keepFocus}
                    />
                  }
                >
                  {space.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
            ) : null}
            {/* Each separator is the list's own <li> beside the items, never inside one. */}
            {page.breadcrumbs.map((b, i) => (
              <Fragment key={b.id ?? `hidden-${i}`}>
                {space || i > 0 ? <BreadcrumbSeparator /> : null}
                <BreadcrumbItem>
                  {b.id ? (
                    <BreadcrumbLink
                      render={
                        <Link
                          to="/c/$slug/work/p/$pageId"
                          params={{ slug: company.slug, pageId: b.id }}
                          search={keepFocus}
                        />
                      }
                    >
                      {b.title}
                    </BreadcrumbLink>
                  ) : (
                    <span title="A page you cannot see">…</span>
                  )}
                </BreadcrumbItem>
              </Fragment>
            ))}
            {space || page.breadcrumbs.length > 0 ? <BreadcrumbSeparator /> : null}
            <BreadcrumbItem>
              <BreadcrumbPage>{page.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {page.can_manage && !taskBody ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    type="button"
                    aria-label={`Kind: ${kindLabel(page.kind)}. Change`}
                    data-slot="page-kind-chip"
                    className="rounded-full focus-visible:outline-2 focus-visible:outline-ring"
                    disabled={manage.isPending || dirty}
                  />
                }
              >
                <Badge variant={page.kind === "page" ? "neutral" : "accent"}>
                  {kindLabel(page.kind)}
                  <ChevronDownIcon aria-hidden="true" />
                </Badge>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-44">
                {PAGE_KINDS.map((k) => (
                  <DropdownMenuItem
                    key={k.value}
                    disabled={k.value === page.kind}
                    onClick={() => manage.mutate({ kind: k.value })}
                  >
                    {k.value === "kb"
                      ? "Reference (KB article)"
                      : k.value === "sop"
                        ? "Procedure (SOP)"
                        : k.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge variant={page.kind === "page" ? "neutral" : "accent"} data-slot="page-kind-chip">
              {kindLabel(page.kind)}
            </Badge>
          )}
          {page.restricted ? (
            <Badge variant="warning">
              <LockIcon aria-hidden="true" />
              Restricted
            </Badge>
          ) : null}
          {page.archived_at ? <Badge variant="neutral">Archived</Badge> : null}
          {page.read_only && !page.archived_at ? (
            <Badge variant="outline" data-slot="page-read-only">
              <LockIcon aria-hidden="true" />
              {editable ? "Read only for others" : "Read only"}
            </Badge>
          ) : !editable && !page.archived_at ? (
            <Badge variant="outline" data-slot="page-read-only">
              Read only
            </Badge>
          ) : null}
          {alsoIn.length ? (
            <Badge variant="outline" data-slot="page-also-in" title={alsoIn.join(", ")}>
              <Share2Icon aria-hidden="true" />
              Also in {alsoIn.length === 1 ? alsoIn[0] : `${alsoIn.length} spaces`}
            </Badge>
          ) : null}
          {!taskBody ? <DocMemoryChip api={api} co={company.id} pageId={page.id} /> : null}
          {dirty ? (
            <Badge variant="info" data-slot="page-unsaved">
              Unsaved changes
            </Badge>
          ) : null}
          {/* One row, never split, actions visible: below 2xl the labels collapse to icons with
              tooltips; only under lg (about 1024px) do Move, Restrict and Archive fold into "⋯"
              (they are in its menu at every width). */}
          <span
            className="ml-auto flex shrink-0 flex-nowrap items-center gap-1.5"
            data-slot="page-actions"
          >
            {/* RECALL-PEOPLE-SURFACES: the selected words, kept in my own memory */}
            <RememberThis
              api={api}
              co={co}
              from="doc"
              size="icon-sm"
              text={() => window.getSelection()?.toString() ?? ""}
            />
            {editable ? (
              <Button
                size="sm"
                disabled={!dirty || save.isPending}
                onClick={saveNow}
                title="Save (⌘S)"
              >
                <SaveIcon aria-hidden="true" />
                Save
              </Button>
            ) : null}
            {manageable && !taskBody ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setNewPage({
                      spaceId: page.space_id,
                      parentPageId: page.id,
                      parentTitle: page.title,
                    })
                  }
                >
                  <PlusIcon aria-hidden="true" />
                  New sub-page
                </Button>
                {/* Its place and sharing are its managers' (`can_manage`): never an edit grant's
                    or a maker who lost edit rights on the space. */}
                {page.can_manage ? (
                  <>
                    <ToolbarAction
                      icon={FolderInputIcon}
                      label="Move"
                      fold
                      onClick={() => setMoving(true)}
                    />
                    {page.kind === "board_note" ? null : (
                      <ToolbarAction
                        icon={Share2Icon}
                        label="Share"
                        onClick={() => setSharingOpen(true)}
                      />
                    )}
                    <ToolbarAction
                      icon={page.restricted ? LockOpenIcon : LockIcon}
                      label={page.restricted ? "Lift restriction" : "Restrict"}
                      fold
                      disabled={restrict.isPending}
                      onClick={() => restrict.mutate(!page.restricted)}
                    />
                  </>
                ) : null}
              </>
            ) : null}
            <ToolbarAction icon={HistoryIcon} label="History" onClick={() => setHistory(true)} />
            {editable && !taskBody && page.can_manage ? (
              <ToolbarAction
                icon={ArchiveIcon}
                label="Archive"
                fold
                onClick={() => setArchiving(true)}
              />
            ) : null}
            {taskBody ? null : (
              <>
                <FavoriteStar
                  company={company}
                  favorite={{ kind: "page", id: page.id }}
                  name={page.title}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={<Button variant="ghost" size="icon-sm" aria-label="Page actions" />}
                  >
                    <EllipsisIcon aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-48">
                    {editable && page.can_manage ? (
                      <DropdownMenuItem onClick={() => setMoving(true)}>
                        <FolderInputIcon aria-hidden="true" />
                        Move
                      </DropdownMenuItem>
                    ) : null}
                    {page.archived_at && page.can_manage !== false ? (
                      <DropdownMenuItem onClick={() => writes.restore.mutate(page)}>
                        <ArchiveRestoreIcon aria-hidden="true" />
                        Restore
                      </DropdownMenuItem>
                    ) : editable && page.can_manage ? (
                      <DropdownMenuItem onClick={() => setArchiving(true)}>
                        <ArchiveIcon aria-hidden="true" />
                        Archive
                      </DropdownMenuItem>
                    ) : null}
                    {page.can_manage ? (
                      <DropdownMenuItem
                        disabled={manage.isPending || dirty}
                        onClick={() => manage.mutate({ read_only: !page.read_only })}
                      >
                        {page.read_only ? (
                          <LockOpenIcon aria-hidden="true" />
                        ) : (
                          <LockIcon aria-hidden="true" />
                        )}
                        {page.read_only ? "Allow edits" : "Make read only"}
                      </DropdownMenuItem>
                    ) : null}
                    {manageable && !taskBody && page.can_manage ? (
                      <DropdownMenuItem
                        disabled={restrict.isPending}
                        onClick={() => restrict.mutate(!page.restricted)}
                      >
                        {page.restricted ? (
                          <LockOpenIcon aria-hidden="true" />
                        ) : (
                          <LockIcon aria-hidden="true" />
                        )}
                        {page.restricted ? "Lift restriction" : "Restrict"}
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem onClick={() => copyDocLink(company.slug, page.id)}>
                      <LinkIcon aria-hidden="true" />
                      Copy link
                    </DropdownMenuItem>
                    {deletable ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => setDeleting(true)}>
                          <Trash2Icon aria-hidden="true" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </span>
        </div>
        {page.archived_at && !taskBody ? (
          <Notice
            tone="info"
            className="mb-4"
            title="This page is archived"
            action={
              <Button
                size="sm"
                variant="secondary"
                disabled={writes.restore.isPending}
                onClick={() => writes.restore.mutate(page)}
              >
                <ArchiveRestoreIcon aria-hidden="true" />
                Restore
              </Button>
            }
          >
            It is out of the space's pages and read only until it is restored.
          </Notice>
        ) : null}
        {/* BIZ-PLAYBOOKS: an SOP (or a linked page) shows its playbook strip, readers included. */}
        {playbook.data && isPlaybook(playbook.data) ? (
          <PlaybookStrip api={api} company={company} page={page} />
        ) : (
          <PageReaders api={api} company={company} page={page} />
        )}
        <UploadSourceBar api={api} company={company} page={page} />
        {stale ? (
          <Notice
            tone="warning"
            className="mb-4"
            title="Someone saved this page after you opened it"
            action={
              <Button size="sm" variant="secondary" onClick={() => void reload()}>
                Reload the page
              </Button>
            }
          >
            Reloading shows their version; copy anything of yours you want to keep first.
          </Notice>
        ) : null}
        {save.error && !stale ? (
          <Notice tone="danger" className="mb-4" title="The page was not saved">
            {save.error instanceof ApiError
              ? (save.error.problem?.detail ?? save.error.problem?.title)
              : "Try again."}
          </Notice>
        ) : null}
        <PageEditor
          key={`${page.id}:${page.version}:${nonce}`}
          title={page.title}
          {...(editable
            ? {
                onTitleChange: (title: string) => {
                  draft.current.title = title;
                  setDirty(true);
                },
                onChange: (doc: { blocks: unknown[] }) => {
                  draft.current.blocks = doc.blocks;
                  setDirty(true);
                },
              }
            : {})}
          initialBlocks={page.body}
          editable={editable}
          docLinks={docLinks}
          changeDelayMs={150}
          width="full"
          className="max-w-prose"
        />
        {page.children.length ? (
          <section
            aria-label="Pages below"
            className="mt-10 max-w-prose border-t border-subtle pt-4"
          >
            <h2 className="mb-2 text-sm font-semibold text-foreground">Pages below</h2>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {page.children.map((c) => (
                <li key={c.id}>
                  <Link
                    to="/c/$slug/work/p/$pageId"
                    params={{ slug: company.slug, pageId: c.id }}
                    search={keepFocus}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <FileTextIcon aria-hidden="true" className="size-4 text-faint" />
                    <span className="min-w-0 flex-1 truncate">{c.title}</span>
                    {c.restricted ? (
                      <LockIcon aria-label="Restricted" className="size-3 text-faint" />
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
      <PageAside api={api} company={company} page={page} />
      <NewDocDialog api={api} company={company} target={newPage} onClose={() => setNewPage(null)} />
      <DeleteDocDialog
        doc={deleting ? page : null}
        onCancel={() => setDeleting(false)}
        onConfirm={(d) => {
          setDeleting(false);
          writes.remove.mutate({
            id: d.id,
            title: d.title,
            leave: () =>
              void navigate({ to: "/c/$slug/work/docs", params: { slug: company.slug } }),
          });
        }}
      />
      {moving ? (
        <MovePageDialog
          api={api}
          company={company}
          page={page}
          spaces={spaces.data?.data ?? []}
          pages={pages.data?.data ?? []}
          open
          onOpenChange={setMoving}
        />
      ) : null}
      {sharingOpen ? (
        <SharePageDialog
          api={api}
          company={company}
          page={page}
          open
          onOpenChange={setSharingOpen}
        />
      ) : null}
      {history ? (
        <PageHistoryDrawer api={api} company={company} page={page} open onOpenChange={setHistory} />
      ) : null}
      <AlertDialog open={archiving} onOpenChange={setArchiving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {page.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves the space's pages; nothing is deleted. The pages under it move up a level.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                archive.mutate();
                setArchiving(false);
              }}
            >
              Archive page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * DOCS-KB "Used by: Riley, Sam": the agents whose knowledge base includes an SOP, policy or KB
 * article (`pages.readers`), each a link to its knowledge; nothing when no agent reads it.
 */
function PageReaders({
  api,
  company,
  page,
}: {
  api: Api;
  company: CompanyAccess;
  page: PageDetail;
}) {
  const read = page.kind === "sop" || page.kind === "policy" || page.kind === "kb";
  const readers = useQuery({ ...pageReadersQuery(api, company.id, page.id), enabled: read });
  const list = read ? (readers.data ?? []) : [];
  if (!list.length) return null;
  return (
    <p
      data-slot="page-readers"
      className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground"
    >
      Used by:
      {list.map((r) => {
        const inner = (
          <>
            <AvatarTile size="xs" kind="agent" name={workerName(r.display_name)} aria-hidden />
            {r.kind === "digital_employee" ? <WorkerBadge kind="de" size="sm" /> : null}
            {workerName(r.display_name)}
          </>
        );
        const cls = "inline-flex items-center gap-1 text-foreground hover:underline";
        return r.kind === "digital_employee" ? (
          <Link
            key={r.membership_id}
            className={cls}
            to="/c/$slug/agents/de/$id"
            params={{ slug: company.slug, id: r.membership_id }}
            search={{ tab: "knowledge" } as never}
          >
            {inner}
          </Link>
        ) : (
          <Link
            key={r.membership_id}
            className={cls}
            to="/c/$slug/agents/me"
            params={{ slug: company.slug }}
          >
            {inner}
          </Link>
        );
      })}
    </p>
  );
}

/**
 * WIKI-LINKS-GRAPH: the editor's [[ picker and doc links. The picker searches the docs the viewer
 * may open (`pages.search`); its "Create page" row makes one in this doc's space when the viewer
 * edits it. A link shows its doc's live title from `pages.links`; a link no doc answers renders
 * unresolved. A task's body has no picker.
 */
function useDocLinks(api: Api, company: CompanyAccess, page: PageDetail): DocLinks | undefined {
  const co = company.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const taskBody = page.kind === "task_body";
  const links = useQuery({ ...pageLinksQuery(api, co, page.id), enabled: !taskBody });
  const outgoing = links.data?.outgoing;
  return useMemo(() => {
    if (taskBody) return undefined;
    const known = new Map<string, DocLinkState>();
    // a `[[Title]]` link: the doc the save resolved its title to (shown by its live title), or
    // missing; a title the API does not name (unsaved, or a hidden doc) reads as unresolved
    const byTitle = new Map<string, DocLinkState & { id: string | null }>();
    for (const l of outgoing ?? []) {
      const state = { title: l.title, missing: l.state === "missing" };
      if (l.page_id) known.set(l.page_id, state);
      byTitle.set(l.title.trim().toLowerCase(), { ...state, id: l.page_id });
    }
    const href = (id: string) => `/c/${company.slug}/work/p/${id}`;
    return {
      search: async (q: string) => {
        const r = await queryClient.fetchQuery(pageSearchQuery(api, co, q));
        return r.data
          .filter((p) => p.id !== page.id)
          .map((p) => ({ id: p.id, title: p.title, href: href(p.id) }));
      },
      ...(page.can_edit
        ? {
            create: async (title: string) => {
              const made = await api.client.pages.create({
                params: { co },
                idempotencyKey: newIdempotencyKey(),
                body: { space_id: page.space_id, title, kind: "page" },
              });
              toast.add({ title: `${made.title} created`, type: "success" });
              void queryClient.invalidateQueries({ queryKey: workKeys.pages(co) });
              return { id: made.id, title: made.title, href: href(made.id) };
            },
          }
        : {}),
      resolve: (id: string) => known.get(id.toLowerCase()) ?? known.get(id),
      resolveTitle: (title: string) => byTitle.get(title.trim().toLowerCase()),
      href,
      open: (id: string) =>
        void navigate({
          to: "/c/$slug/work/p/$pageId",
          params: { slug: company.slug, pageId: id },
          search: keepFocus,
        }),
    };
  }, [
    taskBody,
    outgoing,
    api,
    co,
    company.slug,
    page.id,
    page.space_id,
    page.can_edit,
    queryClient,
    navigate,
  ]);
}

/** Outline and Comments beside the page (below it on narrow screens). */
function PageAside({ api, company, page }: { api: Api; company: CompanyAccess; page: PageDetail }) {
  const [tab, setTab] = useState("outline");
  // RECALL-KB-LINKS: the Related tab exists only when memory has something to say about the doc
  const related = useDocRelated(api, company.id, page.id).data ?? null;
  // WIKI-LINKS-GRAPH: the Graph tab (the doc's local link graph); a task's body has none
  const taskBody = page.kind === "task_body";
  const jump = (item: PageDetail["outline"][number]) => {
    const byId = item.block_id
      ? document.querySelector<HTMLElement>(`[data-id="${CSS.escape(item.block_id)}"]`)
      : null;
    const el =
      byId ??
      Array.from(
        document.querySelectorAll<HTMLElement>("[data-content-type='heading'], h1, h2, h3"),
      ).find((h) => h.textContent?.trim() === item.text);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <aside
      aria-label="Outline and comments"
      data-slot="page-aside"
      className="w-full shrink-0 rounded-lg border border-border bg-card lg:sticky lg:top-4 lg:w-80"
    >
      <Tabs
        value={tab === "related" && !related ? "outline" : tab}
        onValueChange={(v) => setTab(String(v))}
        className="gap-0"
      >
        <TabsList variant="line" aria-label="Page panels" className="px-3">
          <TabsTrigger value="outline">Outline</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
          {related ? <TabsTrigger value="related">Related</TabsTrigger> : null}
          {taskBody ? null : <TabsTrigger value="graph">Graph</TabsTrigger>}
        </TabsList>
        <TabsContent value="outline" className="p-3">
          {page.outline.length ? (
            <ol aria-label="Outline" className="m-0 flex list-none flex-col gap-0.5 p-0">
              {page.outline.map((o, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a heading may have no block id; the outline is fixed per version.
                <li key={`${o.block_id ?? "h"}-${i}`}>
                  <button
                    type="button"
                    onClick={() => jump(o)}
                    className={
                      o.level <= 1
                        ? "w-full truncate rounded-sm px-2 py-1 text-left text-sm font-medium hover:bg-muted"
                        : o.level === 2
                          ? "w-full truncate rounded-sm py-1 pr-2 pl-5 text-left text-sm hover:bg-muted"
                          : "w-full truncate rounded-sm py-1 pr-2 pl-8 text-left text-xs text-secondary hover:bg-muted"
                    }
                  >
                    {o.text}
                  </button>
                </li>
              ))}
            </ol>
          ) : (
            <p className="px-2 py-1 text-sm text-muted-foreground">
              Headings in the page appear here.
            </p>
          )}
        </TabsContent>
        <TabsContent value="comments" className="p-3">
          {tab === "comments" ? (
            <PageComments
              api={api}
              company={company}
              threadId={page.thread_id}
              canComment={page.can_comment}
            />
          ) : null}
        </TabsContent>
        {related ? (
          <TabsContent value="related" className="p-3">
            <RelatedPanel
              api={api}
              company={company}
              pageId={page.id}
              pageVersion={page.version}
              data={related}
            />
          </TabsContent>
        ) : null}
        {taskBody ? null : (
          <TabsContent value="graph" className="p-3">
            {tab === "graph" ? (
              <DocLocalGraph api={api} company={company} pageId={page.id} />
            ) : null}
          </TabsContent>
        )}
      </Tabs>
    </aside>
  );
}

/** The page's thread: comments as chat messages (the `chat` add-on, like a task's thread). */
function PageComments({
  api,
  company,
  threadId,
  canComment,
}: {
  api: Api;
  company: CompanyAccess;
  threadId: string | null;
  /** KB-UPLOAD-2: a read-only doc's readers see the comments, never write one. */
  canComment: boolean;
}) {
  const co = company.id;
  const chat = useFeature("chat");
  const queryClient = useQueryClient();
  const people = useSeatPeople(api, co);
  const key = ["company", co, "work", "page-thread", threadId ?? ""] as const;
  const thread = useQuery({
    queryKey: key,
    queryFn: () =>
      api.client.messages.list({ params: { co, id: threadId ?? "" }, query: { limit: 100 } }),
    enabled: chat.enabled && threadId !== null,
  });
  const post = useMutation({
    mutationFn: (markdown: string) =>
      api.client.messages.create({
        params: { co, id: threadId ?? "" },
        body: { client_id: newIdempotencyKey(), body_markdown: markdown },
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  if (!chat.enabled || threadId === null)
    return (
      <EmptyState
        variant="compact"
        icon={MessagesSquareIcon}
        title="Comments need Chat"
        description="A page's comments are a chat thread; they open here once the Chat add-on is on."
      />
    );
  if (thread.isPending) return <Skeleton className="h-16" />;
  if (thread.isError)
    return <p className="text-sm text-muted-foreground">The comments could not be loaded.</p>;
  const messages: ChatMessage[] = thread.data.data.map((m) => {
    const who = people.nameOf(m.author_membership_id);
    return {
      id: m.id,
      author: { id: m.author_membership_id ?? "system", name: who.name, kind: who.kind },
      createdAt: m.created_at,
      body: m.deleted_at ? "This message was deleted." : m.body_text,
      ...(m.edited_at ? { edited: true } : {}),
    };
  });
  return (
    <div className="flex flex-col gap-3">
      <MessageList
        label="Page comments"
        messages={messages}
        className="max-h-[420px]"
        empty={<p className="text-sm text-muted-foreground">No comments yet.</p>}
      />
      {canComment ? (
        <Composer
          label="Comment on this page"
          placeholder="Write a comment…"
          disabled={post.isPending}
          onSubmit={async (v) => {
            if (v.markdown.trim()) await post.mutateAsync(v.markdown.trim());
          }}
        />
      ) : (
        <p className="m-0 text-xs text-muted-foreground" data-slot="comments-read-only">
          Read only: you can read this doc and its comments.
        </p>
      )}
    </div>
  );
}
